import { useQuery } from '@apollo/react-hooks'
import { MY_PERMISSIONS } from '../api/gql/query'

export type Action = 'VIEW' | 'CREATE' | 'EDIT' | 'DELETE' | 'APPROVE' | 'EXPORT' | 'ADMIN'

export interface MyPermissionsData {
  global: { module: string; actions: Action[] }[]
  perProject: {
    projectId: string
    projectRole: string
    modules: { module: string; actions: Action[]; overridden: boolean }[]
    recordScopedModules: string[]
  }[]
}

/** 1b-B spec §6: on this project the caller reaches `module` only through records he is named on (a contractor's representative). */
export const isRecordScoped = (perms: MyPermissionsData | undefined, projectId: string | undefined, module: string): boolean =>
  !!projectId && !!perms?.perProject.find((p) => p.projectId === projectId)?.recordScopedModules?.includes(module)

export function useMyPermissions(projectId?: string) {
  const { data, loading, refetch } = useQuery<{ myPermissions: MyPermissionsData }>(
    MY_PERMISSIONS,
    { fetchPolicy: 'cache-first' },
  )

  const perms = data?.myPermissions
  const can = (action: Action, module: string): boolean => {
    if (!perms) return false
    const p = projectId ? perms.perProject.find(p => p.projectId === projectId) : undefined
    const m = p?.modules.find(m => m.module === module)
    // An explicit per-user override replaces both axes for this module on this
    // project — it can also REVOKE what the org axis would grant (mirrors backend).
    if (m?.overridden) return m.actions.includes(action)
    const g = perms.global.find(c => c.module === module)
    if (g?.actions.includes(action)) return true
    return !!m?.actions.includes(action)
  }

  const recordScoped = (module: string): boolean => isRecordScoped(perms, projectId, module)

  return { perms, loading, refetch, can, recordScoped }
}

export function useModuleActions(module: string, projectId?: string) {
  const { can, loading, refetch } = useMyPermissions(projectId)
  return {
    loading, refetch,
    canView:    can('VIEW', module),
    canCreate:  can('CREATE', module),
    canEdit:    can('EDIT', module),
    canDelete:  can('DELETE', module),
    canApprove: can('APPROVE', module),
    canExport:  can('EXPORT', module),
    canAdmin:   can('ADMIN', module),
  }
}
